import { Email, Phone } from "@mui/icons-material"
import { Box, Typography } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeLoader } from "components/HaufeLoader/HaufeLoader"
import { useHaufeLoading } from "contexts/HaufeLoadingContext"
import { Contact } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

const Contacts = () => {
  const navigate = useNavigate()
  const { fetchContacts } = useStoreActions((actions) => actions)
  const { contacts } = useStoreState((state) => state)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { haufeOperationType } = useHaufeLoading()

  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchContacts({ fresh: false })
      } catch (err) {
        console.error("Error loading contacts:", err)
        setError(
          "Fehler beim Laden der Kontakte. Bitte versuchen Sie es später erneut."
        )
      } finally {
        setLoading(false)
      }
    }

    loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchContacts({ fresh: true })
    } catch (err) {
      console.error("Error reloading contacts:", err)
      setError(
        "Fehler beim Laden der Kontakte. Bitte versuchen Sie es später erneut."
      )
    } finally {
      setLoading(false)
    }
  }

  const columns: GridColDef<Contact>[] = [
    {
      field: "contactID",
      headerName: "ID",
      flex: 1,
    },
    {
      field: "displayName",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Typography variant="body2">
          {params.row.displayName || "N/A"}
        </Typography>
      ),
    },
    {
      field: "companyName",
      headerName: "Firma",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            height: "100%",
          }}
        >
          <Typography variant="body2">
            {params.row.companyName || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.email && (
            <>
              <Email fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.email}</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "phone1",
      headerName: "Telefon",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.phone1 && (
            <>
              <Phone fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.phone1}</Typography>
            </>
          )}
        </Box>
      ),
    },
  ]

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    navigate(`/data/contacts/${params.row.contactID}`)
  }

  if (loading) {
    // Show Haufe loader if it's a Haufe operation, otherwise regular loader

    return (
      <HaufeLoader
        message={haufeOperationType || "Lade Kontakte aus dem Haufe-System..."}
      />
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <StyledButton
          onClick={handleRetry}
          sx={{ mt: 2 }}
        >
          Retry
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Contacts
      </Typography>
      <DataGrid
        rows={contacts}
        columns={columns}
        onRowClick={handleRowClick}
        autoHeight
        sx={{
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
      />
    </Box>
  )
}

export default Contacts
